import { PayinBank, PayinPaymentMethod } from '~/entities/payin';
import { type PayinPaymentChannel } from '~/entities/payin';
import { type CreateOrderFormValues, type PaymentMethod } from '../model';
import { isSupportedPaymentMethod } from './isSupportedPaymentMethod';

const parseAmountBound = (value?: string) => {
    if (!value) {
        return undefined;
    }

    const amount = Number(value);

    return Number.isFinite(amount) ? amount : undefined;
};

export const isAmountAllowedByPaymentChannel = (channel: PayinPaymentChannel, amount: number) => {
    const min = parseAmountBound(channel.filter?.amount?.min?.value);
    const max = parseAmountBound(channel.filter?.amount?.max?.value);

    if (min !== undefined && amount < min) {
        return false;
    }

    if (max !== undefined && amount > max) {
        return false;
    }

    return true;
};

export const isBankAllowedByPaymentChannel = (channel: PayinPaymentChannel, bank?: PayinBank) => {
    if (!bank || bank === PayinBank.BANK_UNSPECIFIED) {
        return false;
    }

    const allowedBanks =
        channel.filter?.allowedBanks?.filter((allowedBank) => {
            return allowedBank !== PayinBank.BANK_UNSPECIFIED;
        }) ?? [];

    if (allowedBanks.length > 0 && !allowedBanks.includes(bank)) {
        return false;
    }

    return !(channel.filter?.forbiddenBanks ?? []).includes(bank);
};

export const findMatchingPaymentChannel = (
    values: CreateOrderFormValues,
    paymentChannels: PayinPaymentChannel[],
    paymentMethod: PaymentMethod,
) => {
    const amount = values.amount.value;

    if (!amount) {
        return undefined;
    }

    return paymentChannels.find((channel) => {
        const channelMethod = channel.filter?.method;

        if (!isSupportedPaymentMethod(channelMethod) || channelMethod !== paymentMethod) {
            return false;
        }

        if (!isAmountAllowedByPaymentChannel(channel, amount)) {
            return false;
        }

        if (paymentMethod === PayinPaymentMethod.METHOD_SBP) {
            return isBankAllowedByPaymentChannel(channel, values.requisite.sbp.bank);
        }

        return true;
    });
};
